import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import type {
  CreatePlanRequest,
  CreateSaleLineRequest,
  PlanDTO,
  PlanItemDTO,
  PreviewSaleLineRequest,
  WrapperResponse,
  WrapperResponsePlanDTO,
  WrapperResponseListPlanDTO,
  WrapperPageableResponsePlanDTO,
  WrapperResponseVoid,
  SearchPlanFilterSpec,
  ListPlanFilterSpec
} from '../core/models';
import type { Pageable } from './service-common';

export { PlanStatusEnum } from '../core/models';

export type {
  CreatePlanRequest,
  PlanDTO,
  WrapperResponsePlanDTO,
  WrapperResponseListPlanDTO,
  WrapperPageableResponsePlanDTO
} from '../core/models';

@Injectable({
  providedIn: 'root'
})
export class PlanService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  searchPlans(
    spec: SearchPlanFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperResponseListPlanDTO> {
    let params = new HttpParams();
    Object.entries(spec).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, String(value));
      }
    });
    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));
    if (pageable.sort?.length) {
      pageable.sort.forEach(s => { params = params.append('sort', s); });
    }
    return this.http.get<WrapperResponseListPlanDTO>(`${this.apiUrl}/plans`, { params });
  }

  listPlans(
    spec: ListPlanFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperPageableResponsePlanDTO> {
    let params = new HttpParams();
    Object.entries(spec).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach(v => {
          if (v !== undefined && v !== null && v !== '') {
            params = params.append(key, String(v));
          }
        });
      } else if (value !== '') {
        params = params.append(key, String(value));
      }
    });
    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));
    if (pageable.sort?.length) {
      pageable.sort.forEach(s => { params = params.append('sort', s); });
    }
    return this.http.get<WrapperPageableResponsePlanDTO>(`${this.apiUrl}/plans/list`, { params });
  }

  getPlanById(id: number): Observable<WrapperResponsePlanDTO> {
    return this.http.get<WrapperResponsePlanDTO>(`${this.apiUrl}/plans/${id}`);
  }

  createPlan(request: CreatePlanRequest): Observable<WrapperResponsePlanDTO> {
    return this.http.post<WrapperResponsePlanDTO>(`${this.apiUrl}/plans`, request);
  }

  updatePlan(id: number, request: CreatePlanRequest): Observable<WrapperResponsePlanDTO> {
    return this.http.put<WrapperResponsePlanDTO>(`${this.apiUrl}/plans/${id}`, request);
  }

  addPlanItem(planId: number, request: CreateSaleLineRequest): Observable<WrapperResponsePlanDTO> {
    return this.http.post<WrapperResponsePlanDTO>(`${this.apiUrl}/plans/${planId}/items`, request);
  }

  updatePlanItem(planId: number, itemId: number, request: CreateSaleLineRequest): Observable<WrapperResponsePlanDTO> {
    return this.http.put<WrapperResponsePlanDTO>(`${this.apiUrl}/plans/${planId}/items/${itemId}`, request);
  }

  removePlanItem(planId: number, itemId: number): Observable<WrapperResponsePlanDTO> {
    return this.http.delete<WrapperResponsePlanDTO>(`${this.apiUrl}/plans/${planId}/items/${itemId}`);
  }

  previewPlanItem(request: PreviewSaleLineRequest): Observable<WrapperResponse<PlanItemDTO>> {
    return this.http.post<WrapperResponse<PlanItemDTO>>(`${this.apiUrl}/plans/items/preview`, request);
  }

  deletePlan(id: number): Observable<WrapperResponseVoid> {
    return this.http.delete<WrapperResponseVoid>(`${this.apiUrl}/plans/${id}`);
  }
}
